import { useCallback, useRef, useState } from 'react';
import { head, compact } from 'lodash';
import { useIntl } from 'react-intl';

import { DefaultTheme } from '@mui/styles';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import AutoCompleteField, {
  type OptionType,
} from 'src/components/AutoCompleteField';
import {
  useGetTaxClasses,
  useGetCandidateDetails,
  useUpdateCandidateTaxClass,
} from 'src/pages/CandidatesDetailsPage/api';
import LoaderIndicator from 'src/components/LoaderIndicator';

import { CandidateTabProps } from '../CandidateShiftsTab';
import CandidateInfo from '../CandidateInfo/CandidateInfo';
import messages from './messages';

export default function CandidateFinanceTab({ uuid }: CandidateTabProps) {
  const { formatMessage } = useIntl();

  const { data: candidate } = useGetCandidateDetails({
    uuid: uuid,
    enabled: false,
  });

  const { mutate: updateTaxClass } = useUpdateCandidateTaxClass();

  const [taxClassCode, setTaxClassCode] = useState(
    String(candidate?.data?.payroll?.taxClass?.code),
  );

  const { data: taxClasses, isLoading: isLoadingTaxClasses } =
    useGetTaxClasses();

  const handleChangeTaxClass = useCallback(
    (newValue: OptionType | Array<OptionType> | null) => {
      if (!newValue || Array.isArray(newValue)) return;
      setTaxClassCode(newValue.value);
    },
    [],
  );

  const handleUpdateTaxClass = useCallback(() => {
    if (!uuid) return;
    updateTaxClass({ uuid, taxClassCode: taxClassCode });
  }, [taxClassCode]);

  if (!candidate?.data) return null;
  const { firstName, lastName, payroll, healthInsurance } = candidate.data;

  const taxClassSelectorStylesSx = useRef((theme: DefaultTheme) => ({
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      fontSize: theme.spacing(2),
      color: theme.palette.grey[600],
      transform: 'translate(0, 0)',
    },
    '& .MuiInput-root': {
      marginTop: theme.spacing(3.1),
    },
    '& .MuiInput-input': {
      fontWeight: 600,
      fontSize: theme.spacing(2),
    },
  })).current;

  return (
    <Grid container spacing={6} p={2}>
      {isLoadingTaxClasses && <LoaderIndicator fullScreen />}
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.payrollAddress)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.street)}
          value={payroll?.streetName}
        />
        <CandidateInfo
          label={formatMessage(messages.roomNumber)}
          value={payroll?.roomNumber}
        />
        <CandidateInfo
          label={formatMessage(messages.town)}
          value={payroll?.town}
        />
        <CandidateInfo
          label={formatMessage(messages.houseNumber)}
          value={payroll?.streetNumber}
        />
        <CandidateInfo
          label={formatMessage(messages.postCode)}
          value={payroll?.postCode}
        />
        <CandidateInfo
          label={formatMessage(messages.country)}
          value={payroll?.country?.name}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.taxData)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.citizenship)}
          value={payroll?.citizenshipCountry?.name}
        />
        <CandidateInfo
          label={formatMessage(messages.taxId)}
          value={payroll?.taxId}
        />
        <CandidateInfo
          label={formatMessage(messages.religion)}
          value={payroll?.religion?.name}
        />
        <Grid container size={12} alignItems="flex-end">
          <Grid size={{ xs: 3, md: 8 }}>
            <AutoCompleteField
              size="medium"
              label={formatMessage(messages.taxClass)}
              options={taxClasses ?? []}
              value={taxClassCode}
              onChange={handleChangeTaxClass}
              inputCustomSxStyles={taxClassSelectorStylesSx}
            />
          </Grid>
          {taxClassCode !== payroll?.taxClass?.code && (
            <IconButton
              onClick={handleUpdateTaxClass}
              disabled={isLoadingTaxClasses}
            >
              <SaveOutlinedIcon />
            </IconButton>
          )}
        </Grid>
        <CandidateInfo
          label={formatMessage(messages.ucmAsMainEmployer)}
          value={payroll?.isTaxedByCompany ? 'Yes' : 'No'}
        />
        <CandidateInfo
          label={formatMessage(messages.socialSecurityNumber)}
          value={payroll?.socialSecurityNumber}
        />
        <CandidateInfo
          label={formatMessage(messages.spouseReligion)}
          value={payroll?.spouseReligion?.name}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.healthInsurance)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.healthInsuranceType)}
          value={head(healthInsurance)?.type}
        />
        <CandidateInfo
          label={formatMessage(messages.healthInsuranceNumber)}
          value={head(healthInsurance)?.number}
        />
        <CandidateInfo
          label={formatMessage(messages.healthInsuranceProvider)}
          value={head(healthInsurance)?.provider}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 3 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.payrollDetails)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.accountHolder)}
          value={
            payroll?.bankAccountOwner ||
            compact([firstName, lastName]).join(' ')
          }
        />
        <CandidateInfo
          label={formatMessage(messages.iban)}
          value={payroll?.iban}
          wrap
        />
        <CandidateInfo
          label={formatMessage(messages.bic)}
          value={payroll?.bic}
        />
        <CandidateInfo
          label={formatMessage(messages.identityCardNumber)}
          value={payroll?.identityCard}
        />
      </Grid>
    </Grid>
  );
}
